import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { scrollTo } from "../../../../utils/dom/library";
import Viafoura from "../../../../utils/viafoura/library";
import useIsClientHydrated from "../../../../hooks/useIsClientHydrated/index.js";

/**
 * CommentCountMarkup contains the markup that will render the button that displays
 * the number of comments (e.g right under leadMedia in a story).
 * @param {string} storyId the id of the current story
 * @returns {JSX}
 */
function CommentsCountMarkup({ storyId }) {
    const commentsRef = useRef(null);

    useEffect(() => {
        // This useEffect displays the comment count button markup once the Viafoura context is loaded.
        // This prevents the number from flashing, which was previously occurring.
        // The logic ensures that the comment count is only displayed after the context has fully loaded,
        // providing a smoother user experience.
        Viafoura.addOnReadyCallback(() => {
            Viafoura.getUserContext().then(() => {
                commentsRef.current?.classList?.remove("hidden");
            });
        });
    }, [storyId]);

    const commentIcon = (
        <svg viewBox="0 0 32 32" focusable="false" aria-hidden="true">
            <path d="M20.95,17H11a1,1,0,0,0,0,2h10a1,1,0,0,0,0-2Zm2-6H9a1,1,0,0,0,0,2h14a1,1,0,0,0,0-2ZM16,0C7.14,0,0,6.26,0,14a12.55,12.55,0,0,0,6,10.9v7.07l7-4.25a18.71,18.71,0,0,0,3,.25c8.82,0,16-6.25,16-14S24.78,0,16,0Zm0,25.95a16.29,16.29,0,0,1-3.37-.37l-4.7,2.82.06-4.63A11.56,11.56,0,0,1,2,14C2,7.36,8.24,2,16,2s14,5.36,14,12-6.26,12-14,12Zm0,0" />
        </svg>
    );

    // Remove the dot on the storyId
    const id = storyId.replace(".", "");
    return (
        <div
            className="commentCount hidden"
            data-testid="commentCount"
            ref={commentsRef}
        >
            <span className="commentCount-icon">{commentIcon}</span>
            <button
                className="commentCount-link"
                onClick={(e) =>
                    scrollTo(`comments-${id}`, e, "textarea.vf-content")
                }
            >
                <div className="commentCount-text-container">
                    <div className="viafoura">
                        <vf-conversations-count></vf-conversations-count>
                    </div>
                    <div className="commentCount-text" aria-label=" comments">
                        <span> comments</span>
                    </div>
                </div>
            </button>
        </div>
    );
}

/**
 * The CommentCount component.
 *
 * @param {string} storyId the id of the current story
 * @returns {JSX}
 */
export default function CommentCount({ storyId }) {
    const content = useSelector((state) => state.detail.content);
    const shouldDisplayCommentCount = content.displayComments;
    const isClientHydrated = useIsClientHydrated();

    if (!shouldDisplayCommentCount) return null;

    return (
        <div className="comment-count-container">
            {isClientHydrated && <CommentsCountMarkup storyId={storyId} />}
        </div>
    );
}
